import wordpressIcon from 'assets/images/integrations/wordpress.svg';

import type { ConversionRule } from 'modules/pixel/pixelApiTypes';

import { CodeBlock } from '../CodeBlock';
import type { IntegrationContext, IntegrationSnippet, PixelIntegration } from './types';

/**
 * WordPress — install via a header/footer code plugin so the universal pixel lives on every
 * page. Per-rule programmatic fires use {@code CMPIXEL.fire(...)} pasted into specific pages
 * or hooked from theme {@code functions.php}.
 */
export const wordpressIntegration: PixelIntegration = {
  id: 'wordpress',
  label: 'WordPress',
  description:
    'Install via a header/footer code plugin (WPCode, Tracking Code Manager, etc.) — no theme editing required.',
  icon: <img src={wordpressIcon} alt="" className="h-4 w-4" />,
  docsHref: 'https://wordpress.org/plugins/insert-headers-and-footers/',
  renderSetup: (ctx) => (
    <div className="flex flex-col gap-4">
      <Step number={1} title="Install a header/footer code plugin">
        <p className="text-base-xs text-primary-gray-500">
          In WP admin: <strong>Plugins → Add New</strong>, search for{' '}
          <strong>&quot;WPCode – Insert Headers and Footers&quot;</strong> (free, 2M+ installs).
          Install and Activate.
        </p>
        <p className="mt-1 text-base-xs text-primary-gray-500">
          Alternatives: <em>Tracking Code Manager</em>, <em>PixelYourSite</em>, or any plugin that
          lets you inject a script into <code>&lt;head&gt;</code>.
        </p>
      </Step>
      <Step number={2} title="Paste the universal pixel in the Header section">
        <p className="mb-2 text-base-xs text-primary-gray-500">
          Go to <strong>Code Snippets → Header &amp; Footer</strong> (or the equivalent for your
          plugin). Paste this into the <strong>Header</strong> box and Save:
        </p>
        <CodeBlock language="html" code={ctx.pixelScriptTag} />
      </Step>
      <Step number={3} title="Clear caches">
        <p className="text-base-xs text-primary-gray-500">
          If you use a caching plugin (WP Rocket, W3 Total Cache, etc.) or a CDN, clear its cache so
          the new script gets served on the next page load.
        </p>
      </Step>
      <Step number={4} title="(Optional) Fire conversions from specific pages">
        <p className="text-base-xs text-primary-gray-500">
          Trigger-based rules (page_load, URL match) fire automatically. For pages where you need an
          explicit fire (form thank-you, custom JS event), use the per-rule snippet below — paste it
          into the page-specific HTML block or a custom shortcode.
        </p>
      </Step>
    </div>
  ),
  buildSnippetForRule: (rule, ctx) => buildWordPressSnippet(rule, ctx),
};

function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-electric-100 text-[11px] font-semibold text-primary-electric-700">
          {number}
        </span>
        <h4 className="text-base-sm font-medium text-primary-gray-900">{title}</h4>
      </div>
      <div className="pl-7">{children}</div>
    </div>
  );
}

function buildWordPressSnippet(rule: ConversionRule, ctx: IntegrationContext): IntegrationSnippet {
  const lines: string[] = [`  'category': ${JSON.stringify(rule.category)}`];

  if (rule.subcategory) lines.push(`  'subcategory': ${JSON.stringify(rule.subcategory)}`);
  const extractorKeys = rule.extractors ? Object.keys(rule.extractors) : [];

  for (const key of extractorKeys) {
    const placeholder =
      key === 'amount'
        ? '\'<order total>\''
        : key === 'currency'
          ? '\'USD\''
          : key === 'order_number'
            ? '\'<order id>\''
            : '\'\'';

    lines.push(`  '${key}': ${placeholder}`);
  }
  const body = lines.join(',\n');

  const code = [
    '<script>',
    'if (window.CMPIXEL && CMPIXEL.fire) {',
    `  CMPIXEL.fire('${ctx.pixelCode}', {`,
    body,
    '  });',
    '}',
    '</script>',
  ].join('\n');

  return {
    code,
    language: 'html',
    label: 'Paste into the page (or a Custom HTML block / shortcode)',
    note: 'For WooCommerce thank-you pages, prefer the WooCommerce integration tab — it covers the woocommerce_thankyou hook with dynamic order values.',
  };
}
